import { camelCase, isNil, isObject, mergeWith, omitBy, snakeCase } from 'lodash-es';

export function mergeObjects(a: any, b: any) {
    return mergeWith({}, a, b, (objValue, srcValue) => {
        if (Array.isArray(objValue) && Array.isArray(srcValue)) {
            return srcValue;
        } else if (isObject(objValue) && isObject(srcValue)) {
            return mergeObjects(objValue, srcValue);
        } else if (srcValue === null || srcValue === undefined) {
            return objValue;
        }
        return srcValue;
    });
}

/**
 * 객체에서 null, undefined, 빈 배열, 빈 객체를 제거하는 함수
 * @param obj - 필터링할 객체
 * @returns 필터링된 객체
 * @example
 * const data = {
 *   a: 1,
 *   b: null,
 *   c: [],
 *   d: {}
 * }
 * shallowOmitNilAndEmptyArray(data) // { a: 1 }
 */
export const shallowOmitNilAndEmptyArray = <T extends Record<string, any>>(obj: T): { [K in keyof T]: NonNullable<T[K]> } => {
    return omitBy(obj, (value, key) => {
        if (isNil(value) || (Array.isArray(value) && value.length === 0) || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)) {
            return true;
        }
    }) as { [K in keyof T]: NonNullable<T[K]> };
};

/**
 * 객체의 키를 snake_case로 재귀적으로 변환하는 함수
 * @param obj - 변환할 객체 또는 배열
 * @returns 키가 snake_case로 변환된 객체 또는 배열
 * @example
 * const obj = {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   address: { streetName: 'Main St', zipCode: '12345' },
 *   tags: [{ tagName: 'developer' }]
 * };
 * toSnakeCaseObject(obj)
 * // { first_name: 'John', last_name: 'Doe', address: { street_name: 'Main St', zip_code: '12345' }, tags: [{ tag_name: 'developer' }] }
 */
export function toSnakeCaseObject<T>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map(item => toSnakeCaseObject(item)) as T;
    }

    if (isObject(obj) && obj !== null) {
        const result: Record<string, any> = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const transformedKey = snakeCase(key);
                const value = obj[key];
                result[transformedKey] = isObject(value) || Array.isArray(value) ? toSnakeCaseObject(value) : value;
            }
        }
        return result as T;
    }

    return obj;
}

/**
 * 객체의 키를 camelCase로 재귀적으로 변환하는 함수
 * @param obj - 변환할 객체 또는 배열
 * @returns 키가 camelCase로 변환된 객체 또는 배열
 * @example
 * const obj = {
 *   first_name: 'John',
 *   last_name: 'Doe',
 *   address: { street_name: 'Main St', zip_code: '12345' },
 *   tags: [{ tag_name: 'developer' }]
 * };
 * toCamelCaseObject(obj)
 * // { firstName: 'John', lastName: 'Doe', address: { streetName: 'Main St', zipCode: '12345' }, tags: [{ tagName: 'developer' }] }
 */
export function toCamelCaseObject<T>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map(item => toCamelCaseObject(item)) as T;
    }

    if (isObject(obj) && obj !== null) {
        const result: Record<string, any> = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const transformedKey = camelCase(key);
                const value = obj[key];
                result[transformedKey] = isObject(value) || Array.isArray(value) ? toCamelCaseObject(value) : value;
            }
        }
        return result as T;
    }

    return obj;
}
